import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    CircularProgress,
    Alert,
    Button,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Build,
    BookOnline,
    Save,
    Info,
    History,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { SystemSettings } from '../types';
import Layout from '../components/Layout';

const Settings: React.FC = () => {
    const theme = useTheme();
    const [settings, setSettings] = useState<SystemSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await apiService.getSystemSettings();
            setSettings(data);
        } catch (err) {
            console.error('Failed to load settings:', err);
            setError('Failed to load system settings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (field: keyof SystemSettings) => {
        if (settings) {
            setSettings({
                ...settings,
                [field]: !settings[field],
            });
            setSuccess(null);
        }
    };

    const handleSave = async () => {
        if (!settings) return;

        try {
            setSaving(true);
            setError(null);
            await apiService.updateSystemSettings({
                maintenanceMode: settings.maintenanceMode,
                bookingsPaused: settings.bookingsPaused,
                learnMode: settings.learnMode,
            });
            setSuccess('Settings updated successfully.');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to save settings:', err);
            setError('Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        System Settings
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        Configure global application behavior and maintenance modes.
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                        {success}
                    </Alert>
                )}

                <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                    <List sx={{ p: 0 }}>
                        <ListItem sx={{ py: 3, px: 4 }}>
                            <ListItemIcon sx={{ color: 'warning.main', minWidth: 56 }}>
                                <Build fontSize="large" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Under Maintenance
                                    </Typography>
                                }
                                secondary="Put the entire application into maintenance mode. Users will see a maintenance screen and won't be able to access most features."
                            />
                            <Switch
                                edge="end"
                                checked={settings?.maintenanceMode || false}
                                onChange={() => handleToggle('maintenanceMode')}
                                color="warning"
                            />
                        </ListItem>
                        <Divider variant="middle" />
                        <ListItem sx={{ py: 3, px: 4 }}>
                            <ListItemIcon sx={{ color: 'error.main', minWidth: 56 }}>
                                <BookOnline fontSize="large" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Pause Bookings
                                    </Typography>
                                }
                                secondary="Globally disable the creation of new bookings. Existing bookings will still be visible and manageable."
                            />
                            <Switch
                                edge="end"
                                checked={settings?.bookingsPaused || false}
                                onChange={() => handleToggle('bookingsPaused')}
                                color="error"
                            />
                        </ListItem>
                        <Divider variant="middle" />
                        <ListItem sx={{ py: 3, px: 4 }}>
                            <ListItemIcon sx={{ color: 'primary.main', minWidth: 56 }}>
                                <History fontSize="large" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Learn Mode
                                    </Typography>
                                }
                                secondary="Toggle Learn Mode in the application zone."
                            />
                            <Switch
                                edge="end"
                                checked={settings?.learnMode || false}
                                onChange={() => handleToggle('learnMode')}
                                color="primary"
                            />
                        </ListItem>
                    </List>

                    <Box sx={{
                        p: 3,
                        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Info fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {settings?.updatedAt ? new Date(firebaseService.convertTimestamp(settings.updatedAt)).toLocaleString() : 'Never'}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            disabled={saving}
                            onClick={handleSave}
                            sx={{ borderRadius: 2, px: 4 }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </Paper>

                <Box sx={{ mt: 4, p: 3, borderRadius: 4, bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', gap: 2 }}>
                    <History color="info" />
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Security Note
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            These settings take effect immediately across all platforms (iOS, Android, and Web). Ensure you have communicated with the team before putting the app under maintenance.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Layout>
    );
};

export default Settings;
